import { type FC } from 'react';
import { Modal } from '@g2u/ui-kit';
import { Table } from 'antd';
import { TablePaginationConfig, type ColumnsType } from 'antd/es/table';
import { format, isValid, parseISO } from 'date-fns';

import { useSubscriptionInvoices } from 'helpers/hooks/useSubscriptionInvoices';
import { SubscriptionInvoice } from 'types';

const DATE_FORMAT = 'dd MMM yyyy';

const formatDate = (value: string): string => {
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, DATE_FORMAT) : value;
};

const formatCurrency = (value: string): string => `$${Number(value).toFixed(2)}`;

const columns: ColumnsType<SubscriptionInvoice> = [
  {
    key: 'id',
    title: 'Billing ID',
    dataIndex: 'ID',
  },
  {
    key: 'subscription',
    title: 'Subscription',
    dataIndex: 'plan_description',
  },
  {
    key: 'duedate',
    title: 'Due Date',
    render: (_: unknown, record: SubscriptionInvoice) => formatDate(record.duedate),
  },
  {
    key: 'date',
    title: 'Billing Date',
    render: (_: unknown, record: SubscriptionInvoice) => formatDate(record.date),
  },
  {
    key: 'total',
    title: 'Total',
    render: (_: unknown, record: SubscriptionInvoice) => formatCurrency(record.total),
  },
  {
    key: 'received',
    title: 'Payment Received',
    render: (_: unknown, record: SubscriptionInvoice) => formatCurrency(record.payment_received),
  },
];

type Props = {
  isOpen: boolean;
  subscriptionId?: number;
  onCancel: () => void;
};

export const BillingHistoryModal: FC<Props> = ({ isOpen, subscriptionId, onCancel }) => {
  const { invoices, paginationMetadata, isLoading } = useSubscriptionInvoices(subscriptionId, isOpen);

  const pagination: TablePaginationConfig = {
    total: paginationMetadata.total,
    current: paginationMetadata.current_page,
    pageSize: paginationMetadata.pageSize,
    onChange: paginationMetadata.onChange,
    showSizeChanger: false,
    hideOnSinglePage: true,
  };

  return (
    <Modal.$ open={isOpen} title="Billing History" onCancel={onCancel} footer={null} wide dataTestId="billing-history-modal">
      <Table rowKey="ID" columns={columns} dataSource={invoices} loading={isLoading} pagination={pagination} />
    </Modal.$>
  );
};
